import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminRole } from "@/lib/utils";

// Obscured, permanent admin base path — mirrors App.js CONTROL_PANEL_PATH.
const CONTROL_PANEL_PATH = "/control-panel-x9";

function LoadingSplash() {
  return (
    <div
      className="mb-page flex items-center justify-center"
      data-testid="app-loading-splash"
    >
      <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-[#1B2B4B] animate-spin" />
    </div>
  );
}

/**
 * Requires an authenticated session. If the user is authenticated but
 * unverified (broker), redirects to /verification-pending unless
 * `allowUnverified` is set.
 * `adminOnly` restricts to admin role.
 */
export function ProtectedRoute({
  children,
  allowUnverified = false,
  adminOnly = false,
  allowProfileIncomplete = false,
}) {
  const { user, status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <LoadingSplash />;
  if (status !== "authenticated" || !user) {
    const loginTarget = adminOnly ? `${CONTROL_PANEL_PATH}/login` : "/login";
    return <Navigate to={loginTarget} replace state={{ from: location.pathname }} />;
  }

  if (adminOnly && !isAdminRole(user.role))
    return <Navigate to="/dashboard" replace />;

  // Admins belong on the obscured control panel — keep them out of the
  // broker dashboard.
  if (!adminOnly && isAdminRole(user.role) && !location.pathname.startsWith(CONTROL_PANEL_PATH))
    return <Navigate to={CONTROL_PANEL_PATH} replace />;

  // Google-auth users still owe PRC license + phone + image.
  if (!adminOnly && user.role === "broker" && user.profile_incomplete && !allowProfileIncomplete)
    return <Navigate to="/complete-profile" replace />;

  if (!adminOnly && user.role === "broker" && !user.is_verified && !allowUnverified)
    return <Navigate to="/verification-pending" replace />;

  if (allowUnverified && user.is_verified && !user.profile_incomplete && location.pathname === "/verification-pending")
    return <Navigate to="/dashboard" replace />;

  if (allowProfileIncomplete && !user.profile_incomplete && location.pathname === "/complete-profile")
    return <Navigate to="/verification-pending" replace />;

  return children;
}
